import {
  CellId,
  CellLocation,
  CellType,
  ChildCellLocation,
  DereferenceChildCellOptions,
  HexVersionId,
  RelativeCellLocation,
  ResolvedCellLocation,
  SQLCellBlockConfig,
  // eslint-disable-next-line no-restricted-imports
  normalizeCellLocation as commonNormalizeCellLocation,
  // eslint-disable-next-line no-restricted-imports
  resolveCellLocations as commonResolveCellLocations,
} from "@hex/common";

import {
  CellMP,
  hexVersionMPSelectors,
} from "../../slices/hexVersionMPSlice.js";
import { cellSelectionSelectors } from "../../slices/logicViewSlice.js";
import { RootState } from "../../store.js";

interface ResolveCellLocationCommonArgs extends DereferenceChildCellOptions {
  hexVersionId: HexVersionId;
  state: RootState;
}

/**
 * Converts a declarative {@link CellLocation} to
 * a concrete {@link ResolvedCellLocation} so that an
 * insert / move can actually occur or render something
 * else at the potential insert location.
 */
export function resolveCellLocation({
  location,
  ...args
}: ResolveCellLocationCommonArgs & {
  location: CellLocation;
}): ResolvedCellLocation {
  const resolvedLocations = resolveCellLocations({
    count: 1,
    location,
    ...args,
  });
  return resolvedLocations[0];
}

/**
 * Given a single {@link CellLocation} find multiple {@link ResolvedCellLocation}
 * at that point
 *
 * @see {@link resolveCellLocation} for more details on location resolution
 */
export function resolveCellLocations({
  count,
  hexVersionId,
  location,
  state,
  ...options
}: ResolveCellLocationCommonArgs & {
  count: number;
  location: CellLocation;
}): ResolvedCellLocation[] {
  const cells = hexVersionMPSelectors
    .getCellSelectors(hexVersionId)
    .selectFlattenedSorted(state);
  const selectedCellIds = cellSelectionSelectors
    .selectSortedSelectedCells(state, hexVersionId)
    .map((c) => c.id);
  return commonResolveCellLocations({
    count,
    location,
    cells,
    selectedCellIds,
    checkIsSqlBlockCell: (cell: CellMP) =>
      checkIsSqlBlockCell({
        cell,
        hexVersionId,
        state,
      }),
    ...options,
  });
}

function checkIsSqlBlockCell({
  cell,
  hexVersionId,
  state,
}: {
  cell: CellMP;
  hexVersionId: HexVersionId;
  state: RootState;
}): boolean {
  if (cell.cellType === CellType.BLOCK) {
    const cellContents = hexVersionMPSelectors
      .getCellContentSelectors(hexVersionId)
      .selectByCellId(state, cell.id);

    if (cellContents == null) {
      throw new Error(`Unable to find cell contents ${cell.id}`);
    }

    if (
      cellContents.__typename === "BlockCell" &&
      SQLCellBlockConfig.guard(cellContents.blockConfig)
    ) {
      return true;
    }
  }

  return false;
}

export function dereferenceSqlBlockCellChildId({
  cellId,
  hexVersionId,
  state,
}: {
  cellId: CellId;
  hexVersionId: HexVersionId;
  state: RootState;
}): CellId {
  const cell = hexVersionMPSelectors
    .getCellSelectors(hexVersionId)
    .selectById(state, cellId);

  const parentCell =
    cell?.parentCellId != null
      ? hexVersionMPSelectors
          .getCellSelectors(hexVersionId)
          .selectById(state, cell.parentCellId)
      : undefined;

  if (parentCell == null) {
    return cellId;
  }

  const isSqlBlockCell = checkIsSqlBlockCell({
    cell: parentCell,
    hexVersionId,
    state,
  });

  return isSqlBlockCell ? parentCell.id : cellId;
}

export function normalizeCellLocation({
  hexVersionId,
  location,
  state,
}: {
  hexVersionId: HexVersionId;
  location: CellLocation;
  state: RootState;
}): RelativeCellLocation | ChildCellLocation {
  const selectedCellIds = cellSelectionSelectors
    .selectSortedSelectedCells(state, hexVersionId)
    .map((c) => c.id);
  return commonNormalizeCellLocation({
    location,
    selectedCellIds,
  });
}
